import React, { useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  DateRange as DateRangeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

import { formatFinance } from "../../utils/formatters";
import { dateMask } from "../../common/dateMask";
import { getWeekOfMonthDisplay } from "../../utils/dates";

const EMERALD = "#059669";
const EMERALD_LIGHT = "#10b981";

// Animation variants
const tableRowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
    },
  }),
  removed: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 },
  },
};

/**
 * Component displaying costs data in a table with sortable columns
 * @param {Object} props - Component props
 * @param {Array} props.costs - Filtered costs data to display
 * @param {Function} props.getCategoryName - Function to get category name from ID
 * @param {Function} props.getSupplierName - Function to get supplier name from ID
 * @param {Function} props.onEditCost - Handler for editing a cost
 * @param {Function} props.onDeleteCost - Handler for deleting a cost
 * @param {Function} props.onAddCost - Handler for adding a new cost
 * @param {boolean} props.loading - Loading state
 * @returns {JSX.Element} Costs table component
 */
const CostsTable = ({
  costs,
  getCategoryName,
  getSupplierName,
  onEditCost,
  onDeleteCost,
  onAddCost,
  loading,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("date");
  const [order, setOrder] = useState("desc");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Handles sorting request for a column
   * @param {string} property - Column identifier to sort by
   */
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting changes
  };

  /**
   * Creates a comparator function for sorting
   * @param {string} orderBy - Column to sort by
   * @param {string} order - Sort direction ('asc' or 'desc')
   * @returns {Function} Comparator function
   */
  const getComparator = (orderBy, order) => {
    return order === "asc"
      ? (a, b) => compareValues(a, b, orderBy)
      : (a, b) => -compareValues(a, b, orderBy);
  };

  /**
   * Compares two values based on their type
   * @param {Object} a - First row
   * @param {Object} b - Second row
   * @param {string} orderBy - Property to compare
   * @returns {number} Comparison result
   */
  const compareValues = (a, b, orderBy) => {
    // Special handling for category and supplier that need name resolution
    if (orderBy === "category") {
      return (a.categoryName || getCategoryName(a.category)).localeCompare(
        b.categoryName || getCategoryName(b.category)
      );
    }

    if (orderBy === "supplier") {
      const supplierA = a.supplierName || getSupplierName(a.supplier) || "";
      const supplierB = b.supplierName || getSupplierName(b.supplier) || "";
      return supplierA.localeCompare(supplierB);
    }

    // Handle numeric values
    if (orderBy === "value" || orderBy === "week_number") {
      return (a[orderBy] || 0) - (b[orderBy] || 0);
    }

    // Default string comparison
    if (a[orderBy] == null) return -1;
    if (b[orderBy] == null) return 1;

    // Handle dates
    if (orderBy === "date") {
      return new Date(a[orderBy]) - new Date(b[orderBy]);
    }

    // String comparison for other fields
    return String(a[orderBy]).localeCompare(String(b[orderBy]));
  };

  // Sort and slice the data
  const sortedData = useMemo(() => {
    if (!costs || costs.length === 0) return [];

    return [...costs]
      .sort(getComparator(orderBy, order))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [costs, order, orderBy, page, rowsPerPage]);

  // Table header cells configuration
  const headCells = [
    { id: "date", label: "Data", align: "left" },
    { id: "category", label: "Categoria", align: "left" },
    { id: "supplier", label: "Fornecedor", align: "left" },
    { id: "invoice_number", label: "Nota Fiscal", align: "left" },
    { id: "value", label: "Valor", align: "right" },
    { id: "week_number", label: "Semana", align: "center" },
    { id: "actions", label: "Ações", align: "center", disableSorting: true },
  ];

  /**
   * Renders a sortable table header cell
   * @param {Object} headCell - Header cell configuration
   * @returns {JSX.Element} Table header cell
   */
  const renderSortableHeaderCell = (headCell) => {
    const { id, label, align, disableSorting } = headCell;

    return (
      <TableCell key={id} align={align || "left"}>
        {disableSorting ? (
          label
        ) : (
          <TableSortLabel
            active={orderBy === id}
            direction={orderBy === id ? order : "asc"}
            onClick={() => handleRequestSort(id)}
          >
            {label}
          </TableSortLabel>
        )}
      </TableCell>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.5 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(EMERALD, 0.1)}`,
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${alpha(EMERALD, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(EMERALD, 0.03)} 0%, ${alpha(EMERALD_LIGHT, 0.06)} 100%)`,
          }}
        >
          <Typography variant="h6" component="h2" fontWeight={600}>
            Lista de Notas
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(EMERALD, 0.7), fontWeight: 500 }}>
            Exibindo {costs.length} registro de notas
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 4 }}>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </Box>
        ) : costs.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: alpha(EMERALD, 0.06),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <AssignmentIcon
                sx={{ fontSize: 40, color: alpha(EMERALD, 0.4) }}
              />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhuma nota lancada encontrada
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 400, mb: 3 }}
            >
              Tente ajustar seus filtros ou adicionar o primeiro registro de
              nota
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddCost}
              sx={{
                background: `linear-gradient(135deg, ${EMERALD} 0%, ${EMERALD_LIGHT} 100%)`,
                boxShadow: `0 4px 12px ${alpha(EMERALD, 0.3)}`,
                "&:hover": {
                  boxShadow: `0 6px 20px ${alpha(EMERALD, 0.4)}`,
                },
              }}
            >
              Lancar Nota
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>{headCells.map(renderSortableHeaderCell)}</TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {sortedData.map((cost, index) => (
                      <motion.tr
                        key={cost.id}
                        initial="hidden"
                        animate="visible"
                        exit="removed"
                        variants={tableRowVariants}
                        custom={index}
                        component={motion.tr}
                        layoutId={`cost-row-${cost.id}`}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <DateRangeIcon
                              fontSize="small"
                              sx={{
                                mr: 1,
                                color: "action.active",
                                opacity: 0.7,
                              }}
                            />
                            {dateMask(cost.date)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              cost.categoryName ||
                              getCategoryName(cost.category)
                            }
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: alpha(EMERALD, 0.3),
                              color: EMERALD,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {cost.supplierName ||
                            getSupplierName(cost.supplier) ||
                            "-"}
                        </TableCell>
                        <TableCell>{cost.invoice_number || "-"}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            color={
                              cost.value > 100000
                                ? "error.main"
                                : "success.main"
                            }
                          >
                            {formatFinance(cost.value / 100)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getWeekOfMonthDisplay(cost.date)}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onEditCost(cost)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDeleteCost(cost.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={costs.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Registros por Página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </Paper>
    </motion.div>
  );
};

export default CostsTable;
